import ListContainer from '@/components/Record/SubjectContent/ListContainer';
import RadioButton from '@/components/Approval/LeftContainer/PeopleFilter/AllFilterRadios/RadioButton';
import React, { useState } from 'react';
import {
  Approval,
  RecordFilter,
  RecordListType,
  TotalApprovalParams,
} from '@/types/api';
import { useFormContext } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import useSWRInfinite from 'swr/infinite';
import { getApprovalkey, MAX_LIST_SIZE } from '@/components/MobileApproval';
import { APPROVAL_SEARCH_URL } from '@/constant/approval';
import { approvalSearchListFetcher } from '@/hooks/apiHooks/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import Attachment from '@/components/Record/SubjectContent/AttachmentConnect/AttachmentList/Attachment';
import Loading from '@/components/MobileApproval/Body/Loading';
import { apiURL } from '@/constant/apiURL';
import { t } from 'i18next';
import {
  EMPTY_SUBJECT,
  getTalkFlowDisplaySubject,
} from '@/components/Record/util/function';

type Props = {
  searchFocusIndex: number;
  onSelectAttachment: (attachment: any) => void;
  onDataChange?: (data: any[]) => void;
  onResetFocusIndex?: () => void;
};

const AttachmentList = ({
  searchFocusIndex,
  onSelectAttachment,
  onDataChange,
  onResetFocusIndex,
}: Props) => {
  const { watch, getValues, setValue } = useFormContext<RecordFilter>();
  const keyword = watch('searchFields.dataSearch');
  const debouncedValue = useDebounce(keyword, 500);
  const approvalParams: TotalApprovalParams = {
    listSize: 20,
    sortType: 'authoredAt',
    orderType: 'desc',
    keyword: debouncedValue,
  };
  const talkFlowParams: TotalApprovalParams = {
    listSize: 20,
    sortType: 'createdAt',
    orderType: 'desc',
    keyword: debouncedValue,
  };

  const { data, size, setSize, isLoading } = useSWRInfinite<Approval[]>(
    getApprovalkey(APPROVAL_SEARCH_URL, approvalParams),
    approvalSearchListFetcher<Approval>,
  );

  const {
    data: data1,
    size: size1,
    setSize: setSize1,
    isLoading: isLoading1,
  } = useSWRInfinite<RecordListType[]>(
    getApprovalkey(apiURL.TALK_FLOW_DOCUMENT_LIST, talkFlowParams),
    getNestedDataFetcher<RecordListType[]>,
  );

  const [selected, setSelected] = useState('approval');

  const resultData = data?.reduce((prev, next) => prev.concat(next));
  const isReachingEnd = data && data[data.length - 1]?.length < MAX_LIST_SIZE;
  const readMoreData = () => {
    setSize(size + 1);
  };

  const resultData1 = data1?.reduce((prev, next) => prev.concat(next));
  const isReachingEnd1 =
    data1 && data1[data1.length - 1]?.length < MAX_LIST_SIZE;
  const readMoreData1 = () => {
    setSize1(size1 + 1);
  };

  // 현재 표시되는 데이터를 부모에게 전달
  React.useEffect(() => {
    const currentData = selected === 'approval' ? resultData : resultData1;
    if (onDataChange && currentData) {
      const mappedData =
        selected === 'approval'
          ? (currentData as Approval[]).map((item) => ({
              name: item.subject,
              link: item.link,
              type: 'approval' as const,
              id: item.docId,
              createdAt: item.createdAt,
              createBy: item.author.name,
            }))
          : (currentData as RecordListType[]).map((item) => ({
              name: getTalkFlowDisplaySubject(
                item.subject,
                item.suggestedSubject,
                t(EMPTY_SUBJECT),
              ),
              link: '',
              type: item.documentType,
              id: item.id,
              createdAt: item.createdAt,
              createBy: item.createdBy.name,
            }));
      onDataChange(mappedData);
    }
  }, [resultData, resultData1, selected, onDataChange]);

  // 현재 표시되는 실제 데이터 계산
  const currentDisplayData = selected === 'approval' ? resultData : resultData1;

  return (
    <ListContainer
      header={
        <div style={{ display: 'flex', gap: '24px' }}>
          <RadioButton
            name={'search'}
            value={'approval'}
            title={t('품의')}
            checked={selected === 'approval'}
            onChange={() => {
              setSelected('approval');
              onResetFocusIndex?.();
            }}
          />
          <RadioButton
            name={'search'}
            value={'meeting'}
            title={t('문서')}
            checked={selected === 'meeting'}
            onChange={() => {
              setSelected('meeting');
              onResetFocusIndex?.();
            }}
          />
        </div>
      }
      content={
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {selected === 'approval' ? (
            <>
              {(currentDisplayData as Approval[])?.map((item, index) => (
                <Attachment
                  key={item.docId}
                  item={{
                    name: item.subject,
                    link: item.link,
                    type: 'approval',
                    id: item.docId,
                    createdAt: item.createdAt,
                    createBy: item.author.name,
                  }}
                  isFocused={searchFocusIndex === index}
                  onSelect={onSelectAttachment}
                  searchKeyword={debouncedValue}
                />
              ))}
              {data && !isLoading && !isReachingEnd && (
                <Loading readMoreData={readMoreData} />
              )}
            </>
          ) : (
            <>
              {(currentDisplayData as RecordListType[])?.map((item, index) => (
                <Attachment
                  key={item.id}
                  item={{
                    name: getTalkFlowDisplaySubject(
                      item.subject,
                      item.suggestedSubject,
                      t(EMPTY_SUBJECT),
                    ),
                    link: '',
                    type: item.documentType,
                    id: item.id,
                    createdAt: item.createdAt,
                    createBy: item.createdBy.name,
                  }}
                  isFocused={searchFocusIndex === index}
                  onSelect={onSelectAttachment}
                  searchKeyword={debouncedValue}
                />
              ))}
              {data1 && !isLoading1 && !isReachingEnd1 && (
                <Loading readMoreData={readMoreData1} />
              )}
            </>
          )}
        </div>
      }
    />
  );
};
export default AttachmentList;
